import { useTranslation } from 'react-i18next';
import { SolutionOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import TerminateActionModal from '@/components/terminate-action-modal/TerminateActionModal';
import TerminationSummary from '@/components/terminate-action-modal/TerminationSummary';
import { ROLES_PERMISSIONS } from '@/constants';
import { cn } from '@/libs/utils';
import PrivateAction from '@/routes/PrivateAction';
import { DATE_FORMATS } from '@/utils/formUtils';
import ExtendEmploymentModal from '../../_components/actions/ExtendEmploymentModal';

const small = { buttonClassName: '!h-7 !rounded-[50px] !px-2.5 !text-xs' };

/**
 * Top of the "Contract" tile: where the employment stands and the existing
 * extend / terminate flows, the same ones the company "Contracts" tile offers.
 * An ended contract can only be extended (the server does not terminate it),
 * and a termination already in effect cannot be entered a second time — it is
 * shown here instead, with its "Notify" action.
 */
export default function EmploymentActionsCard({ employee, canExtendFromHere }) {
  const { t } = useTranslation(['translations']);
  const tp = (key, opts) =>
    t(`employeeProfile.${key}`, { ns: 'translations', ...opts });

  const es = employee?.employmentSettings || {};
  const archived = employee?.status === 'ARCHIVED';
  // Scheduled: the last working day is still ahead, so the employee is ACTIVE
  // until then — the termination is in effect all the same.
  const scheduledEnd = employee?.scheduledTermination?.endDate;
  const terminated = employee?.status === 'TERMINATED' || !!scheduledEnd;
  const end = es.endDate ? dayjs(es.endDate) : null;
  const daysLeft = end
    ? end.startOf('day').diff(dayjs().startOf('day'), 'day')
    : null;
  const expired = end && daysLeft < 0;
  const canExtend =
    canExtendFromHere ||
    (!archived && es.employmentType === 'limited' && !!end);
  const canTerminate = !archived && !expired && !terminated;

  if (!employee || (!canExtend && !canTerminate && !terminated)) return null;

  const companyId = employee?.company?._id;
  const state = scheduledEnd
    ? tp('employmentUi.leavingOn', {
        date: dayjs(scheduledEnd).format(DATE_FORMATS.DISPLAY),
      })
    : es.employmentType === 'limited' && end
      ? tp('employmentUi.limitedUntil', {
          date: end.format(DATE_FORMATS.DISPLAY),
        })
      : es.employmentType === 'unlimited'
        ? tp('unlimited')
        : tp('special');

  return (
    <section className="setting-card flex flex-wrap items-center gap-2.5 rounded-xl border border-zinc-200 bg-white p-2.5">
      <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-[rgba(var(--accentColor),0.1)] text-[15px] text-accent">
        <SolutionOutlined />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-[12.5px] font-bold text-zinc-900">
          {tp('employmentUi.title')}
        </span>
        <span className="block text-[11.5px] text-zinc-500">
          {state}
          {end && (expired || daysLeft <= 90) && (
            <span
              className={cn(
                'ml-1.5 font-semibold',
                expired ? 'text-red-700' : 'text-amber-700'
              )}
            >
              ·{' '}
              {expired
                ? tp('expiredAgo', { count: -daysLeft })
                : tp('expiresIn', { count: daysLeft })}
            </span>
          )}
        </span>
      </span>
      <PrivateAction {...ROLES_PERMISSIONS.EMPLOYEES.CREATE}>
        <span className="flex gap-1.5">
          {canExtend && (
            <ExtendEmploymentModal
              metadata={small}
              preset={{ employeeId: employee._id, companyId }}
            />
          )}
          {/* Stays mounted once the termination is saved (button hidden),
              so its "Notify" step is not torn down with the button. */}
          {!archived && (canTerminate || terminated) && (
            <TerminateActionModal
              metadata={small}
              employeeId={employee._id}
              companyId={companyId}
              hideTrigger={!canTerminate}
            />
          )}
        </span>
      </PrivateAction>
      {terminated && (
        <PrivateAction {...ROLES_PERMISSIONS.EMPLOYEES.CREATE}>
          <TerminationSummary employeeId={employee._id} />
        </PrivateAction>
      )}
    </section>
  );
}
